import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { cva } from 'class-variance-authority';
/**
 * Card Component (Layer 1 Primitive)
 *
 * A flexible container component with optional header, body, and footer sections.
 * Built on design tokens for consistent elevation and theming.
 *
 * @example
 * ```tsx
 * <Card>
 *   <Card.Header>
 *     <h3>Card Title</h3>
 *   </Card.Header>
 *   <Card.Body>
 *     Card content goes here
 *   </Card.Body>
 *   <Card.Footer>
 *     <Button>Action</Button>
 *   </Card.Footer>
 * </Card>
 * ```
 */
const cardVariants = cva(
// Base styles
'rounded-lg border border-uw-border-default bg-uw-surface-default transition-all duration-200 ease-out', {
    variants: {
        variant: {
            default: 'shadow-md',
            elevated: 'shadow-lg hover:shadow-xl',
            outlined: 'shadow-none',
        },
        padding: {
            none: '',
            sm: 'p-4',
            md: 'p-6',
            lg: 'p-8',
        },
    },
    defaultVariants: {
        variant: 'default',
        padding: 'md',
    },
});
const CardRoot = React.forwardRef(({ children, variant = 'default', padding = 'md', interactive = false, onClick, className = '', ...props }, ref) => {
    const isClickable = interactive || onClick;
    return (_jsx("div", { ref: ref, onClick: onClick, role: isClickable ? 'button' : undefined, tabIndex: isClickable ? 0 : undefined, onKeyDown: isClickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick?.();
                }
            }
            : undefined, className: `${cardVariants({ variant, padding })} ${isClickable ? 'cursor-pointer hover:border-uw-border-strong hover:-translate-y-0.5 hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-uw-interactive-primary focus:ring-offset-2' : ''} ${className}`, ...props, children: children }));
});
CardRoot.displayName = 'Card';
const CardHeader = React.forwardRef(({ children, className = '', ...props }, ref) => {
    return (_jsx("div", { ref: ref, className: `pb-3 border-b border-uw-border-muted ${className}`, ...props, children: children }));
});
CardHeader.displayName = 'Card.Header';
const CardBody = React.forwardRef(({ children, className = '', ...props }, ref) => {
    return (_jsx("div", { ref: ref, className: `py-4 ${className}`, ...props, children: children }));
});
CardBody.displayName = 'Card.Body';
const CardFooter = React.forwardRef(({ children, className = '', ...props }, ref) => {
    return (_jsx("div", { ref: ref, className: `pt-3 border-t border-uw-border-muted ${className}`, ...props, children: children }));
});
CardFooter.displayName = 'Card.Footer';
// Attach subcomponents to Card and export
export const Card = Object.assign(CardRoot, {
    Header: CardHeader,
    Body: CardBody,
    Footer: CardFooter,
});
export { CardHeader, CardBody, CardFooter };
//# sourceMappingURL=Card.js.map